/**
 * Puzzle reward — four jigsaw pieces revealed at lifetime tier milestones.
 *
 * There is no persistent puzzle UI anywhere in the game. The only time a player
 * sees the picture is the moment a new piece lands, so each reveal shows every
 * piece earned so far: piece 1 alone, then the top half, then three of four,
 * then the finished image.
 *
 * State is derived from `playerStats.maxTierEver` rather than stored separately,
 * so it cannot drift out of sync with progression.
 */

/**
 * Tier ids that grant a piece, in order. Positions in the chain (1-based) are
 * 4th / 7th / 9th / 10th — Fish, Butterfly, Pterodactyl, T-rex.
 * @type {number[]}
 */
export const PUZZLE_PIECE_TIER_IDS = [3, 6, 8, 9];

/** Total pieces in the puzzle. */
export const PUZZLE_PIECE_COUNT = PUZZLE_PIECE_TIER_IDS.length;

/**
 * Placement of each cropped piece inside the assembled square, as percentages.
 * Emitted by the cutting script from each piece's alpha bounding box — the tabs
 * overhang the 2x2 grid, so these are not flat quarters.
 * @type {Array<{ key: string, left: number, top: number, width: number, height: number }>}
 */
export const PUZZLE_PIECE_LAYOUT = [
  { key: "puzzlePiece1", left: 0, top: 0, width: 50.5769, height: 63.4615 },
  { key: "puzzlePiece2", left: 36.5385, top: 0, width: 63.4615, height: 50.5769 },
  { key: "puzzlePiece3", left: 0, top: 49.4231, width: 63.4615, height: 50.5769 },
  { key: "puzzlePiece4", left: 49.4231, top: 36.5385, width: 50.5769, height: 63.4615 },
];

/** Headline per piece index (1-based). The last one closes the set. */
export const PUZZLE_REVEAL_HEADLINES = [
  "New puzzle piece!",
  "New puzzle piece!",
  "New puzzle piece!",
  "Puzzle complete!",
];

/**
 * How many pieces a lifetime max tier is worth.
 * @param {number} maxTierEver
 * @returns {number} 0..PUZZLE_PIECE_COUNT
 */
export function puzzlePiecesForMaxTier(maxTierEver) {
  var t = Math.floor(Number(maxTierEver));
  if (!isFinite(t)) return 0;
  var count = 0;
  for (var i = 0; i < PUZZLE_PIECE_TIER_IDS.length; i++) {
    if (t >= PUZZLE_PIECE_TIER_IDS[i]) count += 1;
  }
  return count;
}

/**
 * Piece index (1-based) granted by newly crossing exactly this tier id, or 0.
 * @param {number} tierId
 * @returns {number}
 */
export function puzzlePieceForTierId(tierId) {
  var t = Math.floor(Number(tierId));
  if (!isFinite(t)) return 0;
  var idx = PUZZLE_PIECE_TIER_IDS.indexOf(t);
  return idx < 0 ? 0 : idx + 1;
}
